import { message } from 'antd';

/**
 * Extract từ `InitLogisticPage.tsx` (`openPickingSlipPrintPreview` +
 * helper) sang đây (TCR-1094) để trang danh sách Đơn hàng
 * (`src/pages/Orders`) dùng lại được — trước đó hàm chỉ nhận `PrintJobResult`
 * (dựng từ `EcomOrder[]`), khác `TenantOrder` của trang Orders. `InitLogisticPage.tsx`
 * giờ gọi qua đây thay vì giữ bản riêng, hành vi/HTML in ra giữ NGUYÊN.
 */

export function htmlEscape(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

export function firstString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
      const found = value.find((item): item is string => typeof item === 'string' && item.trim().length > 0);
      if (found) return found.trim();
    }
  }
  return '';
}

export function waitForImagesBeforePrintScript(timeoutMs = 8000): string {
  return `
    <script>
      (function waitForImagesBeforePrint() {
        var images = Array.prototype.slice.call(document.images || []);
        var iframes = Array.prototype.slice.call(document.querySelectorAll('iframe, embed'));
        var pending = images.length + iframes.length;
        var printed = false;
        function doPrint() {
          if (printed) return;
          printed = true;
          window.focus();
          window.print();
        }
        if (pending === 0) {
          setTimeout(doPrint, 100);
          return;
        }
        function markDone() {
          pending -= 1;
          if (pending <= 0) setTimeout(doPrint, 150);
        }
        images.forEach(function (image) {
          if (image.complete && image.naturalWidth > 0) {
            markDone();
            return;
          }
          image.addEventListener('load', markDone, { once: true });
          image.addEventListener('error', markDone, { once: true });
        });
        iframes.forEach(function (frame) {
          var loaded = frame.dataset['ready'] === 'true';
          if (loaded) {
            markDone();
            return;
          }
          frame.addEventListener('load', function () {
            frame.dataset['ready'] = 'true';
            markDone();
          }, { once: true });
          setTimeout(function () {
            frame.dataset['ready'] = 'true';
            markDone();
          }, 3000);
        });
        setTimeout(doPrint, ${timeoutMs});
      })();
    </script>
  `;
}

/** In HTML trong iframe ẩn của tab hiện tại, không tạo popup/tab mới. */
export type PrintLifecycleResult = { status: 'afterprint_received' | 'timeout' };

export interface PrintHtmlInHiddenIframeOptions {
  timeoutMs?: number;
  maxTimeoutMs?: number;
}

const DEFAULT_PRINT_LIFECYCLE_TIMEOUT_MS = 30_000;
const MAX_PRINT_LIFECYCLE_TIMEOUT_MS = 60_000;

export function printHtmlInHiddenIframe(
  html: string,
  title = 'TCR-ERP print',
  options: PrintHtmlInHiddenIframeOptions = {},
): Promise<PrintLifecycleResult> {
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:1px;height:1px;border:0;opacity:0;';
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('tabindex', '-1');
  iframe.title = title;

  const timeoutMs = Math.min(
    Math.max(options.timeoutMs ?? DEFAULT_PRINT_LIFECYCLE_TIMEOUT_MS, 0),
    options.maxTimeoutMs ?? MAX_PRINT_LIFECYCLE_TIMEOUT_MS,
  );

  return new Promise((resolve, reject) => {
    let settled = false;
    let cleaned = false;
    let lifecycleTimer: number | undefined;

    const handleAfterPrint = () => finish({ status: 'afterprint_received' });
    const handleLoad = () => {
      iframe.contentWindow?.addEventListener?.('afterprint', handleAfterPrint, { once: true });
    };
    // A real iframe load error (bad markup, blocked resource, …) is a genuine
    // print failure, not "no afterprint signal yet" — reject instead of
    // resolving `{status: 'timeout'}` so callers can't mistake it for the
    // benign no-signal case and silently treat it as pending/success.
    const handleError = () => fail(new Error('Không thể mở bản in label'));

    const cleanup = () => {
      if (cleaned) return;
      cleaned = true;
      if (lifecycleTimer !== undefined) {
        window.clearTimeout(lifecycleTimer);
        lifecycleTimer = undefined;
      }
      iframe.removeEventListener('load', handleLoad);
      iframe.removeEventListener('error', handleError);
      iframe.contentWindow?.removeEventListener?.('afterprint', handleAfterPrint);
      iframe.remove();
    };

    function finish(result: PrintLifecycleResult): void {
      if (settled) return;
      settled = true;
      cleanup();
      resolve(result);
    }

    function fail(error: Error): void {
      if (settled) return;
      settled = true;
      cleanup();
      reject(error);
    }

    iframe.addEventListener('load', handleLoad, { once: true });
    iframe.addEventListener('error', handleError, { once: true });
    iframe.srcdoc = html;
    document.body.appendChild(iframe);
    lifecycleTimer = window.setTimeout(() => finish({ status: 'timeout' }), timeoutMs);
  });
}

export interface ExportSlipPrintItem {
  sku?: string;
  name?: string;
  erp_name?: string;
  quantity?: number;
  item_type?: string;
  image_url?: string;
  /** Tên combo TMĐT chứa SKU thành phần này. */
  combo_name?: string;
  /** Kệ/vị trí trong kho — không có thì fallback "Kệ Mặc Định". */
  location?: string;
}

export interface ExportSlipPrintOrder {
  order_number: string;
  items: ExportSlipPrintItem[];
}

export interface ExportSlipPrintOptions {
  hide_product_name?: boolean;
  hide_gift_items?: boolean;
  show_print_time?: boolean;
}

export interface ExportSlipRow {
  location: string;
  image_url: string;
  name: string;
  sku: string;
  quantity: number;
  combo_names: string[];
}

/** Dựng danh sách dòng hàng đã gộp trùng sku+tên, cộng dồn số lượng — phần logic thuần, tách khỏi thao tác DOM để test được. */
export function buildExportSlipRows(
  orders: ExportSlipPrintOrder[],
  options: ExportSlipPrintOptions = {},
): ExportSlipRow[] {
  const rowMap = new Map<string, ExportSlipRow>();

  orders.forEach((order) => {
    order.items.forEach((item) => {
      if (options.hide_gift_items && item.item_type === 'gift') return;
      const sku = item.sku || '-';
      const name = item.erp_name || item.name || '-';
      const key = `${sku}::${name}`;
      const location = firstString(item.location) || 'Kệ Mặc Định';
      const imageUrl = firstString(item.image_url);
      const current = rowMap.get(key);
      if (current) {
        current.quantity += Number(item.quantity || 0);
        if (!options.hide_product_name && item.combo_name && !current.combo_names.includes(item.combo_name)) {
          current.combo_names.push(item.combo_name);
        }
        if (!current.image_url && imageUrl) current.image_url = imageUrl;
        if (current.location === 'Kệ Mặc Định' && location !== 'Kệ Mặc Định') current.location = location;
        return;
      }
      rowMap.set(key, {
        image_url: imageUrl,
        location,
        name: options.hide_product_name ? sku : name,
        sku,
        quantity: Number(item.quantity || 0),
        combo_names: !options.hide_product_name && item.combo_name ? [item.combo_name] : [],
      });
    });
  });

  return Array.from(rowMap.values());
}

/** Mở cửa sổ in "PHIẾU XUẤT HÀNG" — HTML/style giữ nguyên bản gốc `openPickingSlipPrintPreview`. */
export function openExportSlipPrintPreview(
  orders: ExportSlipPrintOrder[],
  options: ExportSlipPrintOptions = {},
  documentNumber = '',
): void {
  const printWindow = window.open('', '_blank', 'width=960,height=720');
  if (!printWindow) {
    message.warning('Trình duyệt đã chặn cửa sổ in');
    return;
  }

  const showPrintTime = options.show_print_time !== false;
  const orderNumbers = orders.map((order) => order.order_number).filter(Boolean);
  const rows = buildExportSlipRows(orders, options);

  const rowsHtml = rows.map((row, index) => `
    <tr>
      <td class="center">${index + 1}</td>
      <td>${htmlEscape(row.location)}</td>
      <td class="image-cell">${row.image_url ? `<img src="${htmlEscape(row.image_url)}" alt="${htmlEscape(row.name)}" />` : '<span class="image-placeholder">-</span>'}</td>
      <td>${htmlEscape(row.name)}${row.combo_names.length > 0 ? `<div class="combo-names">Combo TMĐT: ${row.combo_names.map(htmlEscape).join('<br/>')}</div>` : ''}</td>
      <td>${htmlEscape(row.sku)}</td>
      <td class="center">${row.quantity}</td>
    </tr>
  `).join('');

  printWindow.document.write(`
    <html>
      <head>
        <title>Phiếu xuất hàng - ${htmlEscape(documentNumber)}</title>
        <style>
          @page { size: A4; margin: 14mm 16mm; }
          body { margin: 0; padding: 18px 24px; box-sizing: border-box; font-family: "Times New Roman", serif; color: #111; font-size: 12px; }
          .topline { display: flex; justify-content: space-between; font-size: 10px; margin-bottom: 4px; }
          .app-title { text-align: center; font-size: 11px; margin-bottom: 3px; }
          h1 { text-align: center; font-size: 22px; margin: 0 0 10px; letter-spacing: 0; }
          .orders { line-height: 1.55; margin-bottom: 12px; }
          table { width: 100%; border-collapse: collapse; table-layout: fixed; }
          th, td { border: 1px solid #333; padding: 6px 7px; vertical-align: middle; }
          th { text-align: left; font-weight: 700; }
          .center { text-align: center; }
          .image-cell { text-align: center; width: 80px; }
          .image-cell img { max-width: 58px; max-height: 58px; object-fit: contain; }
          .image-placeholder { color: #6b6b6b; }
          .combo-names { margin-top: 3px; color: #666; font-size: 10px; }
          .footer { display: flex; justify-content: space-between; margin-top: 14px; font-size: 10px; }
          @media print { body { padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
        </style>
      </head>
      <body>
        <div class="topline"><span>${showPrintTime ? `${new Date().toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit' })} ${new Date().toLocaleDateString('vi-VN')}` : ''}</span><span></span></div>
        <div class="app-title">TCR-ERP Kho - Ứng dụng quản lý tồn kho trên sàn TMĐT tốt nhất</div>
        <h1>PHIẾU XUẤT HÀNG</h1>
        <div class="orders">
          Phiếu xuất cho ${orders.length} đơn hàng:
          ${htmlEscape(orderNumbers.join(', '))}
        </div>
        <table>
          <thead>
            <tr>
              <th style="width: 42px;">STT</th>
              <th style="width: 120px;">Kệ hàng</th>
              <th style="width: 80px;">Hình ảnh</th>
              <th>Tên SP</th>
              <th style="width: 145px;">Mã sản phẩm</th>
              <th style="width: 70px;">Số lượng</th>
            </tr>
          </thead>
          <tbody>${rowsHtml || '<tr><td colspan="6" class="center">Không có dòng hàng</td></tr>'}</tbody>
        </table>
        <div class="footer">
          <span>https://tcr-erp.local/ecom/fulfillment/init-logistic</span>
          <span>1/1</span>
        </div>
        ${waitForImagesBeforePrintScript()}
      </body>
    </html>
  `);
  printWindow.document.close();
}
